import * as React from 'react';

import { Button, Menu } from '@rematter/ui-web';
import { MoreHorizontal } from 'lucide-react';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { PaymentRecord } from '~/modules/finance/types';

type PaymentActionMenuProps = {
  payment: PaymentRecord;
  onApplyAdvance?: (payment: PaymentRecord) => void;
  onViewDetails?: (payment: PaymentRecord) => void;
};

export const PaymentActionMenu = ({
  payment,
  onApplyAdvance,
  onViewDetails,
}: PaymentActionMenuProps) => {
  const t = useTranslations();

  const hasUnapplied = payment.unappliedAmount.amount !== 0;
  const canApplyToBill =
    payment.paymentType === 'Bill' || payment.paymentType === 'AdvanceOut';
  const canApplyToInvoice =
    payment.paymentType === 'Invoice' || payment.paymentType === 'AdvanceIn';
  const canApply = canApplyToBill || canApplyToInvoice;

  const applyLabel = canApplyToBill
    ? t.payments.detail.actions.applyToBill
    : t.payments.detail.actions.applyToInvoice;

  const menuItems = React.useMemo(
    () => [
      ...(canApply && hasUnapplied && onApplyAdvance
        ? [
            {
              key: 'apply',
              label: applyLabel,
              onAction: () => onApplyAdvance(payment),
            },
          ]
        : []),
      {
        key: 'viewDetails',
        label: t.payments.list.buttons.viewDetails,
        onAction: () => onViewDetails?.(payment),
      },
    ],
    [
      payment,
      onApplyAdvance,
      onViewDetails,
      canApply,
      hasUnapplied,
      applyLabel,
      t,
    ],
  );

  return (
    <div className="@PaymentActionMenu flex justify-center">
      <Menu
        trigger={
          <Button size="icon" icon={<MoreHorizontal />} variant="outline" />
        }
        items={menuItems}
      />
    </div>
  );
};
